import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { CustomerDetailDrawer } from "./CustomerDetailDrawer";

// Beast V1 Checkpoint 4 — Customers panel.
// Lightweight list. Row → CustomerDetailDrawer. Not CRM, not Salesforce.
const commercialLabel = (mode) => {
  if (mode === "managed") return "MANAGED";
  if (mode === "retainer") return "RETAINER";
  return "STANDARD";
};

export const CustomersPanel = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newMode, setNewMode] = useState("standard");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/customers");
      setCustomers(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => (c.display_name || "").toLowerCase().includes(q));
  }, [customers, search]);

  const submitNew = async () => {
    if (!newName.trim()) {
      toast.error("Customer name required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/customers", {
        display_name: newName.trim(),
        commercial_mode: newMode,
      });
      toast.success("Customer created");
      setCreating(false);
      setNewName("");
      setNewMode("standard");
      await load();
      setSelectedId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="customers-panel" className="ur-card p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="ur-eyebrow">Customers</div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#5A5A5A] absolute left-2.5 top-2.5" />
            <input
              data-testid="customers-search"
              className="ur-input pl-8 text-sm min-w-[220px]"
              placeholder="Search customers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            data-testid="new-customer-btn"
            onClick={() => setCreating(true)}
            className="ur-btn-primary text-xs py-2 px-4"
          >
            <Plus className="w-3.5 h-3.5" /> New
          </button>
        </div>
      </div>

      {creating && (
        <div
          data-testid="new-customer-form"
          className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-3 mb-3"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input
              data-testid="new-customer-name"
              className="ur-input text-sm sm:col-span-2"
              placeholder="Customer display name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <select
              data-testid="new-customer-mode"
              className="ur-input text-sm"
              value={newMode}
              onChange={(e) => setNewMode(e.target.value)}
            >
              <option value="standard">Standard</option>
              <option value="managed">Managed</option>
              <option value="retainer">Retainer</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button
              data-testid="new-customer-cancel"
              onClick={() => {
                setCreating(false);
                setNewName("");
              }}
              className="ur-btn-secondary text-xs"
            >
              Cancel
            </button>
            <button
              data-testid="new-customer-submit"
              onClick={submitNew}
              disabled={busy}
              className="ur-btn-primary text-xs"
            >
              Create
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#A3A3A3] py-6 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="customers-empty"
          className="text-sm text-[#A3A3A3] py-6 text-center"
        >
          {customers.length === 0
            ? "No customers yet."
            : "No matches for your search."}
        </div>
      ) : (
        <div className="grid gap-2" data-testid="customers-list">
          {filtered.map((c) => {
            const primary = (c.contacts || []).find((x) => x.is_primary) || (c.contacts || [])[0];
            return (
              <div
                key={c.id}
                data-testid={`customer-row-${c.id}`}
                onClick={() => setSelectedId(c.id)}
                className="ur-card p-3 flex items-center gap-3 cursor-pointer hover:border-[#FF6600]"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{c.display_name}</div>
                  <div className="text-xs text-[#A3A3A3] truncate">
                    {primary ? `${primary.name || primary.email}${primary.email ? ` · ${primary.email}` : ""}` : "No contacts"}
                  </div>
                </div>
                <span
                  data-testid={`customer-mode-badge-${c.id}`}
                  className="shrink-0 inline-flex items-center rounded-full bg-[#FF6600]/12 border border-[#FF6600]/40 text-[#FF6600] text-[10px] font-bold tracking-[0.14em] uppercase px-2 py-0.5"
                >
                  {commercialLabel(c.commercial_mode)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {selectedId && (
        <CustomerDetailDrawer
          customerId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}
    </section>
  );
};
